import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Link2, Info, MessageCircle, Copy, ExternalLink } from "lucide-react";
import { generateReferralUrl, createReferralMessage } from "@/utils/tenantReferralUtils";

interface TenantLandingPage {
  id: string;
  name: string;
  slug: string;
  theme_title: string | null;
  theme_icon: string | null;
  target_audience: string | null;
  reward_description: string | null;
  reward_amount: string | null;
  referrer_message: string;
  is_active: boolean;
}

interface ReferralLinkSelectionProps {
  customerId: string;
  tenantId: string;
  referralCode: string;
}

export default function ReferralLinkSelection({ customerId, tenantId, referralCode }: ReferralLinkSelectionProps) {
  const [landingPages, setLandingPages] = useState<TenantLandingPage[]>([]);
  const [selectedPage, setSelectedPage] = useState<TenantLandingPage | null>(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [customMessage, setCustomMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchLandingPages();
  }, [tenantId]);

  const fetchLandingPages = async () => {
    try {
      const { data, error } = await supabase.rpc('get_accessible_landing_pages_for_user', {
        p_tenant_id: tenantId
      })

      if (error) throw error;
      setLandingPages(data || []);
    } catch (error) {
      console.error('Error fetching landing pages:', error);
      // Don't show toast for this - fail silently and show empty state
      setLandingPages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRecommendClick = (page: TenantLandingPage) => {
    setSelectedPage(page);
    
    // Get tenant data for message generation
    const tenantName = "Ihr Unternehmen"; // This could be fetched from tenant data
    const referralUrl = generateReferralUrl(referralCode, null, false, page.slug);
    const defaultMessage = createReferralMessage(tenantName, referralUrl);
    
    setCustomMessage(defaultMessage);
    setShowMessageModal(true);
  };

  const handleSendMessage = () => {
    if (!selectedPage) return;
    
    const encodedMessage = encodeURIComponent(customMessage);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
    setShowMessageModal(false);
    
    toast({
      title: "WhatsApp geöffnet",
      description: "Sie können jetzt Ihre Empfehlung versenden.",
    });
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(customMessage);
    toast({
      title: "In Zwischenablage kopiert",
      description: "Die Nachricht wurde kopiert.",
    });
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Wähle den passenden Link aus
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-muted rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (landingPages.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Wähle den passenden Link aus
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Link2 className="h-12 w-12 mx-auto mb-4" />
            <h3 className="font-semibold mb-2">Keine Links verfügbar</h3>
            <p className="text-sm">
              Derzeit sind keine themenbasierten Landing Pages aktiviert.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary" />
            Wähle den passenden Link aus
          </CardTitle>
          <CardDescription>
            Schauen Sie sich die Beschreibungen der Links an und wählen Sie den für die Empfehlung am besten passenden aus.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Header Row */}
          <div className="hidden md:grid md:grid-cols-6 gap-4 py-2 px-4 bg-muted/50 rounded-lg text-sm font-medium text-muted-foreground">
            <div>Thema</div>
            <div className="col-span-2">Link passend für</div>
            <div>Deine Prämie</div>
            <div>Gültig</div>
            <div>Aktion</div>
          </div>

          {/* Landing Pages */}
          <div className="space-y-3">
            {landingPages.map((page) => (
              <Card key={page.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="grid md:grid-cols-6 gap-4 items-center">
                    {/* Theme */}
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        {page.theme_icon ? (
                          <span className="text-lg">{page.theme_icon}</span>
                        ) : (
                          <Link2 className="h-4 w-4 text-primary" />
                        )}
                      </div>
                      <div className="md:hidden lg:block">
                        <h4 className="font-medium text-sm">
                          {page.theme_title || page.name}
                        </h4>
                      </div>
                    </div>

                    {/* Target Audience */}
                    <div className="md:col-span-2">
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {page.target_audience || "Für alle geeignet"}
                      </p>
                      <div className="flex items-center gap-1 mt-1">
                        <Info className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          {page.reward_description || "Empfehlungsprämie verfügbar"}
                        </span>
                      </div>
                    </div>

                    {/* Reward */}
                    <div>
                      <Badge variant="secondary" className="bg-primary/10 text-primary font-medium">
                        {page.reward_amount || "Prämie"}
                      </Badge>
                    </div>

                    {/* Valid */}
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-xs text-muted-foreground">Aktiv</span>
                    </div>

                    {/* Action */}
                    <div>
                      <Button 
                        onClick={() => handleRecommendClick(page)}
                        className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-yellow-900 font-medium"
                        size="sm"
                      >
                        Jetzt empfehlen
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Instructions */}
          <Card className="bg-muted/30">
            <CardContent className="p-6">
              <div className="grid md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-primary font-bold">01</span>
                  </div>
                  <h4 className="font-medium mb-2">Aussuchen</h4>
                  <p className="text-sm text-muted-foreground">
                    Schauen Sie sich die Beschreibungen der Links an und wählen Sie den für die Empfehlung am besten passenden aus.
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-primary font-bold">02</span>
                  </div>
                  <h4 className="font-medium mb-2">Klicken</h4>
                  <p className="text-sm text-muted-foreground">
                    Klicken Sie auf den "Jetzt empfehlen" Button. Nachrichtentext prüfen und ggf. anpassen.
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-primary font-bold">03</span>
                  </div>
                  <h4 className="font-medium mb-2">Versenden</h4>
                  <p className="text-sm text-muted-foreground">
                    "Jetzt versenden" klicken und Ihr WhatsApp öffnet sich. Dort einfach die gewünschten Empfehlungen auswählen und versenden.
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-primary font-bold">04</span>
                  </div>
                  <h4 className="font-medium mb-2">Nochmal</h4>
                  <p className="text-sm text-muted-foreground">
                    Wiederholen Sie die Schritte 1-3 um weitere Links zu versenden. So können Sie Ihren Bekannten immer den richtigen Link für Ihre aktuelle Situation senden.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      {/* Message Modal */}
      <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Nachricht anpassen
            </DialogTitle>
            <DialogDescription>
              Sie können die Nachricht vor dem Versenden noch anpassen.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Empfehlungslink für: {selectedPage?.theme_title || selectedPage?.name}</label>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                className="w-full mt-2 p-3 border rounded-lg min-h-[120px] text-sm"
                placeholder="Ihre Nachricht..."
              />
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={copyToClipboard}
                variant="outline" 
                size="sm"
                className="flex-1"
              >
                <Copy className="h-4 w-4 mr-2" />
                Kopieren
              </Button>
              <Button 
                onClick={handleSendMessage}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Jetzt versenden
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}